import type { FreeShippingThresholdQuery } from '#graphql-operations'
import type { MaybeRefOrGetter, Ref } from 'vue'

/**
 * The free-shipping thresholds this storefront advertises (Dashboard → Settings
 * → Free delivery threshold): the storefront's amount and the destination
 * countries that differ from it, in minor units of the channel currency, tax
 * included. `null` means no free delivery there, and every surface that
 * would promise it leaves the promise out.
 */
export interface FreeShippingThresholds {
  amount: number | null
  countries: { countryCode: string, amount: number | null }[]
}

/**
 * Nothing set, or the fetch failed: promise no free delivery rather than a
 * wrong amount. A failed render is cached for at most the hour, and the next
 * one reads the setting again.
 */
function noThresholds(): FreeShippingThresholds {
  return { amount: null, countries: [] }
}

/** The amount for a destination: its own exception if it has one, else the storefront's. */
export function freeShippingThresholdFor(thresholds: FreeShippingThresholds, country: string | undefined): number | null {
  const exception = country ? thresholds.countries.find(entry => entry.countryCode === country) : undefined
  return exception ? exception.amount : thresholds.amount
}

/**
 * The setting itself, fetched once per server render and shared by every
 * caller on the page (the policy tiles, the shipping accordion, the product
 * JSON-LD, the content pages).
 *
 * The same setting the backend prices by: over the amount, the methods ticked
 * for the destination cost nothing. This read is what a shopper sees before
 * there is a cart. Once there is one, the progress bar and checkout go by the
 * per-order quotes, which carry the amount and the backend's verdict, so the
 * cart drawer on every page never has to fetch this. It
 * replaces the copy the storefront used to hard-code in `marketPolicy.ts`, so a
 * threshold change is a Dashboard edit.
 *
 * `server: true` because the product and outfit HTML is edge-cached with the
 * amount in it, and that cached copy is what the backend's purge of
 * `op:freeShippingThreshold` has to reach. The key needs no locale: switching
 * locale always reloads the page.
 */
export function useFreeShippingThresholds(): Ref<FreeShippingThresholds> {
  const { data } = useAsyncData(
    'freeShippingThreshold',
    async (): Promise<FreeShippingThresholds> => {
      try {
        const result = await useGraphqlQuery('freeShippingThreshold')
        // Typed by hand: `useGraphqlQuery` resolves to `any` in this project.
        const setting: FreeShippingThresholdQuery['freeShippingThreshold'] | undefined = result.data?.freeShippingThreshold
        if (!setting)
          return noThresholds()
        return {
          amount: setting.amount ?? null,
          countries: setting.countries.map(entry => ({ countryCode: entry.countryCode, amount: entry.amount ?? null })),
        }
      }
      catch (error) {
        console.error('freeShippingThreshold failed', error)
        return noThresholds()
      }
    },
    {
      server: true,
      default: noThresholds,
      // Several components on one page ask for it: join the request in flight
      // rather than cancel and restart it, as `useAnnouncementBar` does.
      dedupe: 'defer',
      // Serve every later call from what the first produced — on the server
      // too, where `payloadExtraction: false` leaves Nuxt's own cache empty. An
      // explicit refresh still reaches the network.
      getCachedData: (key, nuxtApp, ctx) =>
        (ctx.cause === 'refresh:manual' || ctx.cause === 'refresh:hook')
          ? undefined
          : nuxtApp.payload.data[key] as FreeShippingThresholds | undefined,
    },
  )

  return data
}

/**
 * The threshold for a destination, formatted in the storefront's currency —
 * `null` where there is no amount to quote. Defaults to the storefront country
 * (`de` quotes Germany, or Austria once an Austrian picked it in the header;
 * the checkout still prices the real address). The pick only applies after
 * hydration, so the edge-cached HTML always carries the locale's amount.
 *
 * Whole amounts drop the ",00" — a round threshold reads cleaner inside a
 * sentence — but an amount with cents keeps them, so 99,90 € is never shown as
 * 100 €.
 */
export function useFreeShippingThreshold(country?: MaybeRefOrGetter<string | undefined>) {
  const { n } = useI18n()
  const localeObject = useLocaleObject()
  const storefrontCountry = useStorefrontCountry()
  const thresholds = useFreeShippingThresholds()

  return computed(() => {
    const amount = freeShippingThresholdFor(thresholds.value, toValue(country) ?? storefrontCountry.countryCode.value)
    if (amount === null)
      return null

    const fractionDigits = amount % 100 === 0 ? 0 : 2
    return n(amount / 100, {
      key: 'currency',
      currency: localeObject.value.currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
  })
}
